import { Paperclip, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { Button, IconButton } from '@/components/ui/Button'
import { formatBytes } from '@/utils/format'
import { useUploadLmsFile } from '../api'
import { DIRECT_UPLOAD_THRESHOLD, isVideoFile, uploadDirect } from '../uploads'

export interface Attached {
  id: number
  name: string
  size: number
}

const ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip,.mp3,image/*,video/*'

/** Uploads files as they are chosen; the list of ids is saved with the item. */
export function AttachmentField({
  value,
  onChange,
  disabled,
  max = 10,
}: {
  value: Attached[]
  onChange: (next: Attached[]) => void
  disabled?: boolean
  max?: number
}) {
  const { t } = useTranslation('lms')
  const upload = useUploadLmsFile()
  const input = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState<{ name: string; progress: number } | null>(null)

  const add = async (files: FileList | null) => {
    setError(null)
    let next = value
    for (const file of Array.from(files ?? []).slice(0, max - value.length)) {
      try {
        // Videos and large files go straight to storage; small files through the API.
        const direct = isVideoFile(file) || file.size > DIRECT_UPLOAD_THRESHOLD
        setSending({ name: file.name, progress: 0 })
        const stored = direct
          ? await uploadDirect(file, (progress) => setSending({ name: file.name, progress }))
          : await upload.mutateAsync(file)
        next = [...next, { id: stored.id, name: stored.originalName, size: stored.size }]
        onChange(next)
      } catch (e) {
        const apiError = toApiError(e)
        setError(apiError.violations[0]?.message ?? apiError.message)
      } finally {
        setSending(null)
      }
    }
    if (input.current) input.current.value = ''
  }

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <ul className="space-y-1.5">
          {value.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 rounded-control border border-line bg-surface-muted/50 px-2.5 py-1.5 text-sm"
            >
              <Paperclip className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-ink">{f.name}</span>
              <span className="text-xs text-muted">{formatBytes(f.size)}</span>
              <IconButton
                size="icon-sm"
                variant="ghost"
                icon={<X />}
                label={t('Remove {{name}}', { name: f.name })}
                onClick={() => onChange(value.filter((x) => x.id !== f.id))}
                disabled={disabled}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {sending ? (
        <div className="rounded-control border border-line px-2.5 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Paperclip className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-ink">{sending.name}</span>
            <span className="text-xs text-muted tabular">{Math.round(sending.progress * 100)} %</span>
          </div>
          <div className="mt-1.5 h-1 rounded-full bg-surface-sunken" aria-hidden="true">
            <div
              className="h-full rounded-full bg-brand-500 transition-all"
              style={{ width: `${sending.progress * 100}%` }}
            />
          </div>
        </div>
      ) : null}
      <input
        ref={input}
        type="file"
        multiple
        hidden
        accept={ACCEPT}
        onChange={(e) => void add(e.target.files)}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        leftIcon={<Paperclip />}
        loading={sending !== null}
        disabled={disabled || value.length >= max}
        onClick={() => input.current?.click()}
      >
        {t('Attach files')}
      </Button>
      <p className="text-xs text-muted">
        {t(
          'Documents, slides, spreadsheets, images, audio or zip up to 20 MB; videos and larger files up to 2 GB (downloaded, not streamed).',
        )}
      </p>
      {error ? <p className="text-xs text-danger-700">{error}</p> : null}
    </div>
  )
}
